'use client'
import { Download as DownloadIcon, FileDown, ExternalLink, Bell, BookOpen } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { LeadCaptureForm } from './lead-capture-form'
import type { Download } from '@/lib/copy/downloads'

// Wraps LeadCaptureForm with a success state that hands the user the
// gated asset URL. Used by /downloads/[slug], the form posts to
// /api/leads as usual, then swaps in a "Download" CTA pointed at the
// real file. For coming-soon entries the form converts to a notify
// list — same /api/leads endpoint, different success copy and no file
// to hand over.

export function DownloadGate({ download }: { download: Download }) {
  const isUpcoming = download.status === 'coming-soon'

  return (
    <LeadCaptureForm
      source={`/downloads/${download.slug}`}
      toolSlug={download.slug}
      defaultIntent={isUpcoming ? 'newsletter' : 'try-tool'}
      successContent={
        isUpcoming ? (
          <div className="text-center">
            <span className="mx-auto grid h-14 w-14 place-items-center rounded-full bg-primary/10 text-primary">
              <Bell className="h-6 w-6" />
            </span>
            <p className="mt-4 font-display text-xl font-semibold tracking-tight">
              You&rsquo;re on the list.
            </p>
            <p className="mt-1.5 text-sm text-muted-foreground">
              Expected window: {download.expectedWindow ?? 'soon'}. We&rsquo;ll email you the day
              the {download.format} drops, with the methodology and the dataset.
            </p>
            <p className="mt-4 text-xs text-muted-foreground">
              Nothing else in your inbox, no list-sharing.
            </p>
          </div>
        ) : (
          <div className="text-center">
            <span className="mx-auto grid h-14 w-14 place-items-center rounded-full bg-primary/10 text-primary">
              <FileDown className="h-6 w-6" />
            </span>
            <p className="mt-4 font-display text-xl font-semibold tracking-tight">Ready when you are.</p>
            <p className="mt-1.5 text-sm text-muted-foreground">
              We&rsquo;ve emailed you the link too. {download.format} · {download.weight}
            </p>
            <div className="mt-5 flex flex-wrap items-center justify-center gap-3">
              {download.hubUrl ? (
                <>
                  <Button asChild size="lg">
                    <a href={download.hubUrl}>
                      <BookOpen className="h-4 w-4" /> Read the full series
                    </a>
                  </Button>
                  {download.file && (
                    <Button asChild variant="outline" size="lg">
                      <a href={download.file} target="_blank" rel="noopener noreferrer" download>
                        <DownloadIcon className="h-4 w-4" /> Condensed {download.format}
                      </a>
                    </Button>
                  )}
                </>
              ) : (
                download.file && (
                  <>
                    <Button asChild size="lg">
                      <a href={download.file} target="_blank" rel="noopener noreferrer" download>
                        <DownloadIcon className="h-4 w-4" /> Download the {download.format}
                      </a>
                    </Button>
                    <Button asChild variant="outline" size="lg">
                      <a href={download.file} target="_blank" rel="noopener noreferrer">
                        Open in new tab <ExternalLink className="h-4 w-4" />
                      </a>
                    </Button>
                  </>
                )
              )}
            </div>
            <p className="mt-4 text-xs text-muted-foreground">
              Reproduce with attribution to <a className="underline hover:text-foreground" href="https://idukki.io">idukki.io</a>.
            </p>
          </div>
        )
      }
    />
  )
}
